import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, Spinner, Empty } from "@/components/UIKit";
import StatusBadge from "@/components/StatusBadge";
import { fmtKwh, fmtDate } from "@/lib/format";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Zap } from "lucide-react";

const chartAxis = { stroke: "#3f3f46", fontSize: 10, tickLine: false, axisLine: false };
const chartTooltip = {
  contentStyle: { background: "#0a0a0a", border: "1px solid #27272a", borderRadius: 8, fontSize: 12 },
  labelStyle: { color: "#a1a1aa" }, itemStyle: { color: "#fafafa" },
};

export default function PortalForbruk() {
  const [days, setDays] = useState(90);
  const [data, setData] = useState([]);
  const [meters, setMeters] = useState([]);

  useEffect(() => {
    api.get(`/portal/consumption?days=${days}`).then(({ data }) => setData(data));
  }, [days]);
  useEffect(() => {
    api.get("/portal/metering-points").then(({ data }) => setMeters(data));
  }, []);

  const total = data.reduce((s, d) => s + d.kwh, 0);
  const avg = data.length ? total / data.length : 0;

  return (
    <div data-testid="portal-forbruk-page">
      <PageHeader
        title="Forbruk"
        subtitle="Historisk energiforbruk for dine målepunkter"
        actions={
          <div className="flex items-center gap-1 bg-zinc-900/60 rounded-md p-1 border border-zinc-900">
            {[30, 90, 180, 365].map((d) => (
              <button key={d} onClick={() => setDays(d)}
                className={`px-3 py-1 rounded text-xs transition-colors ${days === d ? "bg-zinc-800 text-zinc-100" : "text-zinc-500 hover:text-zinc-200"}`}>{d}d</button>
            ))}
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <Card className="p-5">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Totalt forbruk</div>
          <div className="font-heading text-3xl tabular-nums text-amber-400">{fmtKwh(total)}</div>
        </Card>
        <Card className="p-5">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Snitt per dag</div>
          <div className="font-heading text-3xl tabular-nums">{fmtKwh(avg)}</div>
        </Card>
        <Card className="p-5">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Målepunkter</div>
          <div className="font-heading text-3xl tabular-nums">{meters.length}</div>
        </Card>
      </div>

      <Card className="p-5 mb-4">
        <div className="font-heading font-medium mb-3">Forbrukskurve</div>
        {data.length === 0 ? <Empty title="Ingen forbruksdata" /> : (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data}>
              <defs>
                <linearGradient id="pf" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#141414" vertical={false} />
              <XAxis dataKey="date" {...chartAxis} />
              <YAxis {...chartAxis} />
              <Tooltip {...chartTooltip} formatter={(v) => [fmtKwh(v), ""]} />
              <Area type="monotone" dataKey="kwh" stroke="#f59e0b" strokeWidth={2} fill="url(#pf)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card className="overflow-hidden">
        <div className="p-4 border-b border-zinc-900 font-heading font-medium">Dine målepunkter</div>
        {meters.length === 0 ? <Empty title="Ingen målepunkter" /> : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase text-zinc-500 bg-zinc-950/60">
                <th className="py-2 px-4 font-medium">MPID</th>
                <th className="py-2 px-4 font-medium">Nettselskap</th>
                <th className="py-2 px-4 font-medium">Adresse</th>
                <th className="py-2 px-4 font-medium">Prisområde</th>
                <th className="py-2 px-4 font-medium">Årsforbruk</th>
                <th className="py-2 px-4 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {meters.map((mp) => (
                <tr key={mp.id} className="border-t border-zinc-900">
                  <td className="py-2 px-4 font-mono text-xs text-emerald-400 flex items-center gap-1.5"><Zap className="h-3 w-3" />{mp.mpid}</td>
                  <td className="py-2 px-4 text-zinc-300">{mp.grid_company}</td>
                  <td className="py-2 px-4 text-zinc-400 text-xs">{mp.address}, {mp.postal_code} {mp.city}</td>
                  <td className="py-2 px-4"><span className="text-xs bg-zinc-800 px-1.5 py-0.5 rounded">{mp.price_area}</span></td>
                  <td className="py-2 px-4 tabular-nums text-zinc-300">{fmtKwh(mp.annual_consumption_kwh)}</td>
                  <td className="py-2 px-4"><StatusBadge value={mp.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
